import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, inject } from '@angular/core';
import { PasswordInputComponent } from '../../../shared/widgets/inputs/password-input/password-input.component';
import { GeneralInputComponent } from '../../../shared/widgets/inputs/general-input/general-input.component';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GeneralButtonDirective } from '../../../shared/directives/buttons/general-button.directive';
import {
  clearToastAction,
  errorToastAction,
  loadingToastAction,
} from '../../../core/store/actions/toast.actions';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import { AuthService } from '../../../core/services/auth.service';
import { LocalService } from '../../../core/services/local.service';
import { RedirectService } from '../../../core/services/redirect.service';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { environment } from '../../../../environments/environment';
import { BackButtonRouteComponent } from '../../../shared/widgets/back-button-route/back-button-route.component';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { superAdminRoleList } from '../../../core/models/roleList';

@Component({
  selector: 'app-superadmin-login',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    PasswordInputComponent,
    GeneralInputComponent,
    ReactiveFormsModule,
    RouterModule,
    GeneralButtonDirective,
    BackButtonRouteComponent,
    NzCheckboxModule,
  ],
  templateUrl: './superadmin-login.component.html',
  styleUrl: './superadmin-login.component.css',
})
export class SuperadminLoginComponent /* implements OnInit */ {
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  redirectService = inject(RedirectService);
  store = inject(Store<AppState>);
  routeTo = injectQueryParams('routeTo');
  reCaptchaV3Service = inject(ReCaptchaV3Service);
  localService = inject(LocalService);

  recaptchaSiteKey = environment.recaptchaSiteKey;

  //Form control
  authForm = new FormGroup({
    email: new FormControl<string | null>(null, [
      Validators.required,
      Validators.email,
    ]),
    password: new FormControl<string | null>(null, [Validators.required]),
    recaptcha: new FormControl<string | null>(null, [Validators.required]),
    rememberMe: new FormControl<boolean>(false),
  });

  // ngOnInit() {
  //   const savedEmail = this.localService.getData('remember_me_email');
  //   if (savedEmail) {
  //     this.authForm.patchValue({
  //       email: savedEmail,
  //       rememberMe: true,
  //     });
  //   }
  // }

  // API call (Login)
  mutation = injectMutation(() => ({
    mutationFn: (loginData: { [key: string]: any }) => {
      const { rememberMe, ...loginPayload } = loginData;
      return this.authService.login(loginPayload);
    },
    onSuccess: ({ id, ...others }) => {
      // Invalidate and refetch by using the client directly

      // Invalidate and refetch by using the client directly

      const email = this.authForm.value.email;
      const rememberMe = this.authForm.value.rememberMe;
      if (rememberMe && email) {
        this.localService.saveData('remember_me_email', JSON.stringify(email));
      } else {
        this.localService.removeData('remember_me_email');
      }

      this.store.dispatch(clearToastAction());

      const role = (others?.roles || [])?.[0];

      if (superAdminRoleList.includes(role)) {
        this.authForm.reset();
        this.authService.setUserData(others);
      }

      // Check if there's a saved redirect URL
      const redirectUrl = this.redirectService.getAndClearRedirectUrl();

      if (redirectUrl) {
        // If there's a saved URL, navigate to it
        this.router.navigateByUrl(redirectUrl);
        return;
      }

      // Otherwise, use default role-based navigation
      switch (role) {
        case 'Governor':
          this.router.navigate(['/governor']);
          break;
        case 'SuperAdmin':
        case 'SuperAdminViewOnly':
          this.router.navigate(['/admin']);
          break;
        case 'Admin':
        case 'Administrator':
          this.router.navigate(['/admin-auth']);
          break;
        case 'Expert System':
          this.router.navigate(['/tsp']);
          break;

        default:
          this.router.navigate(['/']);
          break;
      }
      //OR use the queryClient that is injected into the component
      //this.queryClient.invalidateQueries({ queryKey: ['todos'] })
    },
    onError: (error: any) => {
      this.store.dispatch(
        errorToastAction({
          autohide: true,
          delay: 7000,
          placement: 'top-end',
          message:
            error?.error?.message ||
            'An error occurred while logging in. Try again shortly',
        }),
      );
    },
  }));

  onSubmit() {
    if (
      this.authForm.get('email')?.invalid ||
      this.authForm.get('password')?.invalid
    ) {
      this.authForm.get('email')?.markAsTouched();
      this.authForm.get('password')?.markAsTouched();
      return;
    }

    this.store.dispatch(
      loadingToastAction({
        autohide: false,
        delay: 7000,
        placement: 'top-end',
        message: 'Verifying security check. Please wait...',
      }),
    );

    this.reCaptchaV3Service
      .executeAsPromise(this.recaptchaSiteKey, 'login', {
        useGlobalDomain: false,
      })
      .then((token) => {
        this.authForm.patchValue({ recaptcha: token });
        this.store.dispatch(
          loadingToastAction({
            autohide: false,
            delay: 7000,
            placement: 'top-end',
            message: 'Logging in. Please wait...',
          }),
        );
        this.mutation.mutate(this.authForm.value);
      })
      .catch((error) => {
        console.error('reCAPTCHA error:', error);
        this.store.dispatch(
          errorToastAction({
            autohide: true,
            delay: 7000,
            placement: 'top-end',
            message: 'reCAPTCHA verification failed. Please try again.',
          }),
        );
      });
  }
}
